/**
 * Helper function to normalize date fields in API responses
 * @param {any} data - Data to normalize (could be object, array, or primitive)
 * @returns {any} - Normalized data with standardized date formats
 */
import { showToast } from './toastUtils';
import { logout } from './tokenManager';

const normalizeDates = (data) => {
  if (!data) return data;
  
  // Handle arrays by processing each item
  if (Array.isArray(data)) {
    return data.map(item => normalizeDates(item));
  }
  
  // Only process objects (not null and not Date objects)
  if (data !== null && typeof data === 'object' && !(data instanceof Date)) {
    const result = {};
    
    // Process each property in the object
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key];
        
        // Check if it's a date-like string with microseconds
        if (typeof value === 'string' && 
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+/.test(value)) {
          try {
            // Normalize ISO timestamp by removing microseconds
            result[key] = value.split('.')[0] + 'Z';
          } catch (e) {
            // If there's any error, keep the original value
            result[key] = value;
          }
        } else if (typeof value === 'object') {
          // Recursively process nested objects
          result[key] = normalizeDates(value);
        } else {
          // Keep other values as is
          result[key] = value;
        }
      }
    }
    
    return result;
  }
  
  // Return primitives and other types unchanged
  return data;
};

/**
 * API Interceptor
 * 
 * Utility to intercept API requests and responses, handle errors,
 * and automatically redirect to login on authentication failures.
 */

/**
 * Make an authenticated API request
 * 
 * @param {string} url - The API URL
 * @param {Object} options - Request options (method, headers, body)
 * @returns {Promise} - Response promise
 */
export const apiRequest = async (url, options = {}) => {
  // Set default options
  const defaultOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  };
  
  // Merge options
  const requestOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
      'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
    },
  };
  
  try {
    const response = await fetch(url, requestOptions);
    
    // Handle 401 Unauthorized
    if (response.status === 401) {
      // Clear all tokens and redirect to login
      logout();
      showToast('Your session has expired. Please log in again.', 'error');
      
      // Redirect to login
      window.location.href = '/login';
      return Promise.reject({ message: 'Authentication failed' });
    }
    
    // Parse response
    const data = await response.json().catch(() => ({
      status: response.status,
      message: response.statusText,
    }));
    
    // Check if response is successful
    if (!response.ok) {
      // Show error toast
      showToast(data.message || 'An error occurred', 'error');
      return Promise.reject(data);
    }
    
    // Normalize date fields in the response
    const normalizedData = normalizeDates(data);
    
    return normalizedData;
  } catch (error) {
    // Handle network errors
    const errorMessage = error.message || 'Network error';
    showToast(errorMessage, 'error');
    return Promise.reject({ message: errorMessage });
  }
};

/**
 * Post data to API
 * 
 * @param {string} url - The API URL
 * @param {Object} data - Request body data
 * @param {Object} options - Additional request options
 * @returns {Promise} - Response promise
 */
export const apiPost = (url, data, options = {}) => {
  return apiRequest(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * Get data from API
 * 
 * @param {string} url - The API URL
 * @param {Object} options - Additional request options
 * @returns {Promise} - Response promise
 */
export const apiGet = (url, options = {}) => {
  return apiRequest(url, {
    method: 'GET',
    ...options,
  });
};

/**
 * Put data to API
 * 
 * @param {string} url - The API URL
 * @param {Object} data - Request body data
 * @param {Object} options - Additional request options
 * @returns {Promise} - Response promise
 */
export const apiPut = (url, data, options = {}) => {
  return apiRequest(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * Delete data from API
 * 
 * @param {string} url - The API URL
 * @param {Object} options - Additional request options
 * @returns {Promise} - Response promise
 */
export const apiDelete = (url, options = {}) => {
  return apiRequest(url, {
    method: 'DELETE',
    ...options,
  });
};

export default {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  delete: apiDelete,
  request: apiRequest,
}; 